'use client';

import Header from '@/components/common/Header';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

export default function PrivacyPolicyPage() {
  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50">
        {/* Hero */}
        <section className="bg-gradient-to-br from-green-900 to-slate-900 py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-400/30 rounded-full text-green-300 text-sm font-semibold mb-4">
              <Icon name="ShieldCheckIcon" size={16} />
              Legal & Compliance
            </div>
            <h1 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">Privacy Policy</h1>
            <p className="text-green-200/70 text-base">GenCarbon ESG Pvt. Ltd. · Last updated: May 2026</p>
          </div>
        </section>

        {/* Content */}
        <section className="py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12 space-y-10">

              {/* Introduction */}
              <div>
                <h2 className="font-heading font-bold text-xl text-gray-900 mb-3">1. Introduction</h2>
                <p className="text-gray-600 leading-relaxed text-sm">
                  GenCarbon ESG Pvt. Ltd. ("GenCarbon", "we", "us", or "our") operates the website <strong>www.gencarbon.in</strong> and related digital properties. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services. Please read this policy carefully. If you disagree with its terms, please discontinue use of our site.
                </p>
              </div>

              {/* Information We Collect */}
              <div>
                <h2 className="font-heading font-bold text-xl text-gray-900 mb-3">2. Information We Collect</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-800 text-sm mb-2">2.1 Information You Provide Directly</h3>
                    <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-2">
                      <li>Name, email address, phone number when filling lead capture forms</li>
                      <li>Company name, designation, and sector information</li>
                      <li>Email address when requesting research papers, lead magnets, or speaking briefs</li>
                      <li>Messages and enquiries submitted via contact forms</li>
                      <li>WhatsApp communications initiated by you</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 text-sm mb-2">2.2 Information Collected Automatically</h3>
                    <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-2">
                      <li>IP address, browser type, operating system, and device information</li>
                      <li>Pages visited, time spent, and navigation patterns via Google Analytics 4</li>
                      <li>UTM parameters from advertising campaigns (utm_source, utm_medium, utm_campaign)</li>
                      <li>Cookie data as described in our Cookie Policy below</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* How We Use Your Information */}
              <div>
                <h2 className="font-heading font-bold text-xl text-gray-900 mb-3">3. How We Use Your Information</h2>
                <ul className="list-disc list-inside text-gray-600 text-sm space-y-2 ml-2">
                  <li>To respond to your enquiries and provide ESG consulting services</li>
                  <li>To send requested resources, research papers, and lead magnet downloads</li>
                  <li>To send you relevant updates about carbon credits, BRSR compliance, and ESG regulations (with your consent)</li>
                  <li>To improve our website, services, and user experience</li>
                  <li>To measure marketing campaign effectiveness via GA4 conversion funnels</li>
                  <li>To comply with legal obligations under Indian law (IT Act 2000, DPDP Act 2023)</li>
                </ul>
              </div>

              {/* Data Sharing */}
              <div>
                <h2 className="font-heading font-bold text-xl text-gray-900 mb-3">4. Data Sharing & Disclosure</h2>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  We do not sell, trade, or rent your personal information to third parties. We may share data with:
                </p>
                <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-2">
                  <li><strong>Service Providers:</strong> Supabase (database), Google Analytics (analytics), email service providers — all bound by data processing agreements</li>
                  <li><strong>Legal Requirements:</strong> When required by Indian law, court order, or government authority</li>
                  <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, with prior notice to users</li>
                </ul>
              </div>

              {/* Cookies */}
              <div>
                <h2 className="font-heading font-bold text-xl text-gray-900 mb-3">5. Cookies & Tracking Technologies</h2>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  We use cookies and similar tracking technologies to enhance your experience. Types of cookies we use:
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-green-50">
                        <th className="border border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">Cookie Type</th>
                        <th className="border border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">Purpose</th>
                        <th className="border border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['Essential', 'Site functionality, session management', 'Session'],
                        ['Analytics (GA4)', 'Traffic analysis, conversion tracking', '2 years'],
                        ['Preference', 'Language, cookie consent status', '1 year'],
                        ['Marketing', 'UTM campaign tracking', '90 days'],
                      ]?.map(([type, purpose, duration], i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="border border-gray-200 px-3 py-2 text-gray-700 font-medium">{type}</td>
                          <td className="border border-gray-200 px-3 py-2 text-gray-600">{purpose}</td>
                          <td className="border border-gray-200 px-3 py-2 text-gray-600">{duration}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-gray-500 text-xs mt-3">You can manage cookie preferences through our Cookie Consent Banner or your browser settings.</p>
              </div>

              {/* Data Retention */}
              <div>
                <h2 className="font-heading font-bold text-xl text-gray-900 mb-3">6. Data Retention</h2>
                <p className="text-gray-600 text-sm leading-relaxed">
                  We retain personal data for as long as necessary to provide our services and comply with legal obligations. Lead form data is retained for 3 years. Analytics data is retained per Google Analytics default settings (14 months). You may request deletion of your data at any time by contacting us.
                </p>
              </div>

              {/* Your Rights */}
              <div>
                <h2 className="font-heading font-bold text-xl text-gray-900 mb-3">7. Your Rights (DPDP Act 2023 & GDPR)</h2>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">Under applicable data protection laws, you have the right to:</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    ['Access', 'Request a copy of your personal data we hold'],
                    ['Correction', 'Request correction of inaccurate data'],
                    ['Erasure', 'Request deletion of your personal data'],
                    ['Portability', 'Receive your data in a structured format'],
                    ['Objection', 'Object to processing for marketing purposes'],
                    ['Withdraw Consent', 'Withdraw consent at any time without affecting prior processing'],
                  ]?.map(([right, desc], i) => (
                    <div key={i} className="flex gap-3 p-3 bg-green-50 rounded-lg border border-green-100">
                      <Icon name="CheckCircleIcon" size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-gray-800 text-xs">{right}</p>
                        <p className="text-gray-600 text-xs">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-gray-500 text-xs mt-3">To exercise any of these rights, email us at <a href="mailto:nupurbanker@gmail.com" className="text-green-600 hover:underline">nupurbanker@gmail.com</a></p>
              </div>

              {/* Security */}
              <div>
                <h2 className="font-heading font-bold text-xl text-gray-900 mb-3">8. Data Security</h2>
                <p className="text-gray-600 text-sm leading-relaxed">
                  We implement industry-standard security measures including SSL/TLS encryption, Supabase Row Level Security (RLS) policies, and access controls. However, no method of transmission over the internet is 100% secure. We encourage you to use strong passwords and report any suspected security breaches to us immediately.
                </p>
              </div>

              {/* Children */}
              <div>
                <h2 className="font-heading font-bold text-xl text-gray-900 mb-3">9. Children's Privacy</h2>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal information from minors. If you believe we have inadvertently collected such information, please contact us immediately.
                </p>
              </div>

              {/* Changes */}
              <div>
                <h2 className="font-heading font-bold text-xl text-gray-900 mb-3">10. Changes to This Policy</h2>
                <p className="text-gray-600 text-sm leading-relaxed">
                  We may update this Privacy Policy periodically. We will notify you of significant changes by posting the new policy on this page with an updated "Last updated" date. Continued use of our website after changes constitutes acceptance of the updated policy.
                </p>
              </div>

              {/* Contact */}
              <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                <h2 className="font-heading font-bold text-xl text-gray-900 mb-3">11. Contact Us</h2>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  For any privacy-related questions, data requests, or concerns, please contact our Data Protection Officer:
                </p>
                <div className="space-y-2 text-sm">
                  <p className="text-gray-700"><strong>GenCarbon ESG Pvt. Ltd.</strong></p>
                  <p className="text-gray-600">Dr. Nupur Mukherjee, Founder & Chief Scientist</p>
                  <p className="text-gray-600">
                    Email: <a href="mailto:nupurbanker@gmail.com" className="text-green-600 hover:underline font-medium">nupurbanker@gmail.com</a>
                  </p>
                  <p className="text-gray-600">
                    Website: <a href="https://www.gencarbon.in" className="text-green-600 hover:underline font-medium">www.gencarbon.in</a>
                  </p>
                </div>
              </div>

              {/* Back link */}
              <div className="pt-4 border-t border-gray-100">
                <Link href="/homepage" className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 text-sm font-medium transition-colors">
                  <Icon name="ArrowLeftIcon" size={16} />
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
